import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { uiLog } from "@/services/contentEngine/contentLogger";
import { isAutoGenerateBulkRunning } from "@/hooks/useAutoGenerateBulk";
import { useCanAccessSideprojects } from "@/hooks/useCanAccessSideprojects";

export type Workspace = "firma" | "sideprojects";

export interface WorkspaceMeta {
  id: Workspace;
  label: string;
  description: string;
  emoji: string;
  colorClass: string;
}

export const WORKSPACES: WorkspaceMeta[] = [
  {
    id: "firma",
    label: "Firma / Zaplecżówki",
    description:
      "Mapy pod klientów, projekty komercyjne, publikacje rozliczane. Widok produkcyjny.",
    emoji: "🏢",
    colorClass: "text-blue-500",
  },
  {
    id: "sideprojects",
    label: "Sponsoraki / Sideprojects",
    description:
      "Projekty eksperymentalne, osobiste, boczne inicjatywy. Widok dev.",
    emoji: "🎨",
    colorClass: "text-pink-500",
  },
];

const LS_KEY = "app_workspace";
const DEFAULT_WS: Workspace = "firma";

function readStored(): Workspace {
  if (typeof window === "undefined") return DEFAULT_WS;
  const stored = localStorage.getItem(LS_KEY);
  if (stored === "firma" || stored === "sideprojects") return stored;
  return DEFAULT_WS;
}

/**
 * Hook zarządzający aktualnym workspace'em (firma/sideprojects).
 *
 * - Persistence w localStorage (klucz `app_workspace`)
 * - Zmiana → invalidate wszystkie queries thematic_* + toast
 * - Cross-tab sync przez storage event
 * - Dostęp do `sideprojects` (Sponsoraki) TYLKO dla userów z flagą
 *   `allowed_users.can_access_sideprojects` (ta sama flaga steruje RLS w bazie).
 *   Nieuprawniony: nie widzi karty, nie może przełączyć, ewentualny stary wpis
 *   w localStorage degraduje do `firma`.
 */
export function useWorkspace() {
  const [rawWorkspace, setWorkspaceState] = useState<Workspace>(readStored);
  const { canAccess } = useCanAccessSideprojects();
  const qc = useQueryClient();

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY) setWorkspaceState(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Efektywny workspace. Degradujemy `sideprojects`→`firma` dopiero po POTWIERDZENIU
  // braku dostępu (canAccess===false), a nie podczas ładowania RPC — żeby uprawniony
  // z zapisanym `sideprojects` nie mrugał widokiem firma→sideprojects przy wejściu.
  const workspace: Workspace =
    rawWorkspace === "sideprojects" && canAccess === false ? "firma" : rawWorkspace;

  // Sprzątanie localStorage gdy potwierdzono brak dostępu a siedzi tam `sideprojects`.
  useEffect(() => {
    if (canAccess === false && readStored() === "sideprojects") {
      localStorage.setItem(LS_KEY, "firma");
      setWorkspaceState("firma");
      qc.invalidateQueries({ queryKey: ["thematic-maps"] });
      qc.invalidateQueries({ queryKey: ["websites"] });
    }
  }, [canAccess, qc]);

  const setWorkspace = useCallback(
    (next: Workspace) => {
      if (next === workspace) return;

      // Gate: brak dostępu do Sponsoraków → blokada przełączenia.
      if (next === "sideprojects" && canAccess !== true) {
        toast.error("Brak dostępu do workspace Sponsoraki");
        return;
      }

      // KOREKTA 6.5: workspace switch zablokowany podczas bulk auto-generate.
      // Wszystkie operacje LLM/SERP/FAL by zaczely lecieć na inne konto rozliczeniowe.
      if (isAutoGenerateBulkRunning()) {
        toast.error("Zatrzymaj bulk przed zmianą workspace", {
          description: `Aktywny workspace: ${workspace}. Mapy by zaczęły się rozliczać na inne konto.`,
        });
        return;
      }

      localStorage.setItem(LS_KEY, next);
      setWorkspaceState(next);
      uiLog.public("Workspace", `Przełączono na: ${next}`);
      // Invalidate wszystkie query'e które filtrują po workspace
      qc.invalidateQueries({ queryKey: ["thematic-maps"] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      // Fire storage event same-tab (React listeners)
      window.dispatchEvent(new StorageEvent("storage", { key: LS_KEY, newValue: next }));

      // Phase 3 C3 (2026-05-26): redirect do listy map gdy user jest na
      // KONKRETNEJ mapie. Workspace switcher zmienia per-workspace API keys —
      // generowanie na mapie firma w workspace sideprojects = koszty na zlym
      // koncie + nieswiadome krzyzowanie projektow. Pelne window.location.href
      // (NIE navigate) zeby React Query / context / state caly reset.
      // Z listy map (/mapy-tematyczne bez ID) ani z innych stron (dashboard,
      // artykuly, ustawienia) NIE redirectujemy — query invalidation wystarczy.
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      const mapDetailRe = /^\/mapy-tematyczne\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
      if (mapDetailRe.test(path)) {
        window.location.href = "/mapy-tematyczne";
      }
    },
    [workspace, qc, canAccess],
  );

  const meta = WORKSPACES.find((w) => w.id === workspace) || WORKSPACES[0];
  // Przełącznik pokazuje kartę Sponsoraki tylko z potwierdzonym dostępem.
  const all =
    canAccess === true ? WORKSPACES : WORKSPACES.filter((w) => w.id !== "sideprojects");

  return {
    workspace,
    setWorkspace,
    meta,
    all,
  };
}
